// Story 7.3 (ADO-1139) — the listing-side availability hook (AC1/AC2).
// Story 7-FU-F (ADO-1219) — reacts to a live depot switch (AC3).
//
// A listing (Browse's CategoryListing, Search's results) calls this with the
// SKUs it is rendering: the hook batch-loads their availability at the session
// depot and ensures the shared real-time socket exists. When no session depot
// is configured (`resolveSessionDepotId` returns null) it no-ops entirely: no
// fetch, no socket, no badges. Cards read the store per SKU themselves; this
// hook only drives population.

import { useEffect } from 'react';
import { resolveSessionDepotId } from './session-depot';
import { ensureAvailabilitySocket, disconnectAvailabilitySocket } from './availability-socket';
import { useAvailabilityStore } from './useAvailabilityStore';
import { useSessionStore } from '../auth/useSessionStore';

export function useAvailability(skuCodes: readonly string[]): void {
  // Key the effect on the VALUE of the sku list, not its identity — listings
  // rebuild the array every render; refetching on referential change would loop.
  const skuKey = [...skuCodes].sort().join(',');
  // Subscribe to the active depot so a live switch (DepotPicker → setActiveDepot)
  // re-runs the effect (7-FU-F AC3): 7.3 keyed this effect on the SKU list ONLY,
  // so a depot change never re-fetched or moved the socket. Resolving through
  // `resolveSessionDepotId` keeps the UUID guard + null-degrade in one place.
  const activeDepot = useSessionStore((s) => s.activeDepot);

  useEffect(() => {
    const depotId = resolveSessionDepotId(activeDepot);
    // No active depot (unauthenticated / signed out / terminal refresh): drop the
    // shared socket so it does not linger in the old depot's room feeding stale
    // pushes into the store (7-FU-F review). An empty SKU list is NOT a switch to
    // "no depot" — keep the socket for the surface that still tracks SKUs.
    if (depotId === null) {
      disconnectAvailabilitySocket();
      return;
    }
    if (skuKey === '') return;
    const skus = skuKey.split(',');
    // On a switch the store's loadForSkus resets the previous depot's entries +
    // re-seeds trackedSkus, and ensureAvailabilitySocket moves the socket to the
    // new depot's room (7-FU-F Task 3).
    void useAvailabilityStore.getState().loadForSkus(depotId, skus);
    void ensureAvailabilitySocket(depotId);
  }, [skuKey, activeDepot]);
}
